import type { ErrorEvent, MonitoringSink, MonitoringUser } from './types';

/**
 * Writes errors to the browser console.
 *
 * **This is not monitoring.** Nobody reads a user's console, and React already
 * logs errors a boundary catches. Its purpose is to let the capture plumbing —
 * boundary, global handlers, dedupe, redaction — ship and be reviewed before
 * the destination is decided, and to give local development something visible.
 *
 * Deliberately prints the redacted event rather than the original error: it is
 * the only way to see what a remote sink *would* have transmitted, which is the
 * check worth having.
 */
export const createConsoleSink = (): MonitoringSink => {
  let user: MonitoringUser | undefined;

  return {
    name: 'console',
    report: (event: ErrorEvent) => {
      const context = {
        ...(user ? { userId: user.id, ...user.properties } : {}),
        ...event.properties,
      };
      // console.error, not warn: this is an error, and the level is what any
      // future console-scraping or browser-extension triage will filter on.
      console.error(
        `[monitoring] ${event.fatal ? 'fatal' : 'handled'} ${event.name}: ${event.message}`,
        { stack: event.stack, ...context },
      );
    },
    setUser: (next: MonitoringUser) => {
      user = next;
    },
  };
};
